import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useStaffAuth } from "@/context/StaffAuthContext";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, KeyRound, BookOpen, Globe2, BadgeCheck, UserRound, Calendar as CalendarIcon } from "lucide-react";

const STAFF_TYPE_LABEL = {
  teacher: { en: "Teacher", zh: "教师" },
  assistant_teacher: { en: "Assistant Teacher", zh: "助教" },
  admin_staff: { en: "Admin Staff", zh: "管理人员" },
};
const STATUS_LABEL = {
  active: { en: "Active", zh: "在职", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  on_holiday: { en: "On Holiday", zh: "休假中", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  resigned: { en: "Resigned", zh: "已离职", tone: "bg-stone-100 text-stone-600 border-stone-300" },
};
const GENDER_LABEL = {
  female: { en: "Female", zh: "女" }, male: { en: "Male", zh: "男" },
  other: { en: "Other", zh: "其他" }, prefer_not: { en: "Prefer not to say", zh: "不公开" },
};
const EDUCATION_LABEL = {
  high_school: { en: "High School", zh: "高中" }, bachelor: { en: "Bachelor", zh: "学士" },
  master: { en: "Master", zh: "硕士" }, phd: { en: "Ph.D.", zh: "博士" },
};

function ChangePasswordCard({ refresh, mustChange }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(!!mustChange);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (next.length < 4) { toast.error(lang === "zh" ? "新密码至少 4 位" : "New password must be at least 4 characters"); return; }
    if (next === "00000") { toast.error(lang === "zh" ? "新密码不能是默认密码" : "New password cannot be the default"); return; }
    setBusy(true);
    try {
      await api.post("/staff/me/password", { current_password: current, new_password: next });
      toast.success(lang === "zh" ? "密码已更新" : "Password updated");
      setOpen(false); setCurrent(""); setNext("");
      await refresh();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5" data-testid="staff-password-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[hsl(var(--primary))]" />
          <p className="font-display text-base">{lang === "zh" ? "更改密码" : "Change password"}</p>
        </div>
        <Button size="sm" variant="outline" onClick={()=>setOpen(v=>!v)} data-testid="staff-password-toggle" className="rounded-full">{open ? (lang === "zh" ? "收起" : "Close") : (lang === "zh" ? "更改" : "Change")}</Button>
      </div>
      {mustChange && (
        <p className="mt-3 text-xs rounded-lg bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2" data-testid="staff-must-change-banner">
          {lang === "zh" ? "您正在使用默认密码 00000。为安全起见请立即更改。" : "You're using the default password 00000. Please change it for security."}
        </p>
      )}
      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">{lang === "zh" ? "当前密码" : "Current password"}</Label>
            <Input type="password" value={current} onChange={(e)=>setCurrent(e.target.value)} data-testid="staff-password-current" className="mt-1" />
          </div>
          <div><Label className="text-xs">{lang === "zh" ? "新密码" : "New password"}</Label>
            <Input type="password" value={next} onChange={(e)=>setNext(e.target.value)} data-testid="staff-password-new" className="mt-1" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button onClick={submit} disabled={busy} data-testid="staff-password-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{busy ? "…" : (lang === "zh" ? "保存" : "Save")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TeacherClasses({ lang }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/staff/me/classes"); setRows(data); }
      catch (e) { toast.error(getErrorDetail(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden" data-testid="teacher-classes-card">
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center gap-2">
        <CalendarIcon className="w-4 h-4 text-[hsl(var(--primary))]" />
        <p className="font-display text-base">{lang === "zh" ? "我的课表" : "My classes"}</p>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">· {rows.length}</span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--muted))]/50">
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
            <th className="py-2 px-4">{lang === "zh" ? "课程名" : "Class"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "项目" : "Program"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "上课日" : "Days"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "时间" : "Time"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "等级" : "Level"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "学生" : "Students"}</th>
            <th className="py-2 px-4">{lang === "zh" ? "状态" : "Status"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} data-testid={`teacher-class-${r.id}`} className="border-t border-[hsl(var(--border))]/60">
              <td className="py-2 px-4">{lang === "zh" && r.name_zh ? r.name_zh : r.name}</td>
              <td className="py-2 px-4">{r.program}</td>
              <td className="py-2 px-4 text-xs">{(r.days_of_week || []).join(" · ")}</td>
              <td className="py-2 px-4 text-xs">{r.time_slot}</td>
              <td className="py-2 px-4 text-xs">{r.level}</td>
              <td className="py-2 px-4 text-xs">{r.seats_taken}/{r.seats_total}</td>
              <td className="py-2 px-4 text-xs"><span className="px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] uppercase tracking-wider">{r.status}</span></td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={7} className="py-10 text-center text-[hsl(var(--muted-foreground))] text-xs italic">{lang === "zh" ? "暂未安排课程。" : "No classes assigned yet."}</td></tr>
          )}
          {loading && <tr><td colSpan={7} className="py-10 text-center text-[hsl(var(--muted-foreground))] text-xs">…</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function StaffPage() {
  const { staff, ready, logout, refresh } = useStaffAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  if (!ready) return <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">…</div>;
  if (!staff) return <Navigate to="/staff/login" replace />;
  const status = STATUS_LABEL[staff.status] || STATUS_LABEL.active;
  const typ = STAFF_TYPE_LABEL[staff.staff_type] || STAFF_TYPE_LABEL.teacher;

  return (
    <main data-testid="staff-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--primary))]">{typ[lang === "zh" ? "zh" : "en"]}</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-2">
            {lang === "zh" ? "您好，" : "Welcome, "}<span className="text-[hsl(var(--primary))]">{lang === "zh" && staff.name_zh ? staff.name_zh : staff.name_en}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 text-xs rounded-full border ${status.tone}`} data-testid="staff-status-pill">{status[lang === "zh" ? "zh" : "en"]}</span>
          <Button size="sm" variant="outline" onClick={async ()=>{ await logout(); navigate("/"); }} data-testid="staff-logout-btn" className="rounded-full"><LogOut className="w-4 h-4 mr-1.5" />{lang === "zh" ? "退出" : "Logout"}</Button>
        </div>
      </div>

      <section className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7 rounded-2xl border border-[hsl(var(--border))] bg-white p-6" data-testid="staff-basic-info">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" />{lang === "zh" ? "基本信息" : "Basic information"}</p>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "英文姓名" : "English name"}</dt><dd className="font-display text-base">{staff.name_en}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "中文姓名" : "Chinese name"}</dt><dd className="font-display text-base">{staff.name_zh || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "性别" : "Gender"}</dt><dd>{GENDER_LABEL[staff.gender]?.[lang === "zh" ? "zh" : "en"] || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "类别" : "Staff type"}</dt><dd>{typ[lang === "zh" ? "zh" : "en"]}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Globe2 className="w-3 h-3" />{lang === "zh" ? "国籍" : "Nationality"}</dt><dd>{staff.nationality}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "用户名" : "Username"}</dt><dd className="font-mono text-xs">{staff.username}</dd></div>
            <div className="sm:col-span-2"><dt className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "邮箱" : "Email"}</dt><dd>{staff.email}</dd></div>
          </dl>
        </div>
        <div className="md:col-span-5 rounded-2xl border border-[hsl(var(--border))] bg-white p-6 space-y-4" data-testid="staff-qualifications">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" />{lang === "zh" ? "学历与资格" : "Education & qualifications"}</p>
          <div><p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "学历" : "Education"}</p><p className="font-display text-base">{EDUCATION_LABEL[staff.education_level]?.[lang === "zh" ? "zh" : "en"] || "—"}</p></div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "教学证书" : "Teaching certs"}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(staff.teaching_certs || []).map(c => <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30">{c.replace("_", " ")}</span>)}
              {(!staff.teaching_certs || staff.teaching_certs.length === 0) && <span className="text-xs text-[hsl(var(--muted-foreground))] italic">—</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "语言证书" : "Language proficiency"}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(staff.language_certs || []).map(c => <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border border-[hsl(var(--secondary))]/30">{c}</span>)}
              {(!staff.language_certs || staff.language_certs.length === 0) && <span className="text-xs text-[hsl(var(--muted-foreground))] italic">—</span>}
            </div>
          </div>
          {staff.experience && (
            <div><p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center gap-1"><BookOpen className="w-3 h-3" />{lang === "zh" ? "相关经验" : "Experience"}</p><p className="text-sm whitespace-pre-wrap text-[hsl(var(--foreground))]/85 mt-1">{staff.experience}</p></div>
          )}
        </div>
      </section>

      <section className="mt-8"><ChangePasswordCard refresh={refresh} mustChange={staff.must_change_password} /></section>

      {staff.staff_type === "teacher" && (
        <section className="mt-8"><TeacherClasses lang={lang} /></section>
      )}
    </main>
  );
}
